import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/apiConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return token && user ? { token, user: JSON.parse(user) } : null;
  });
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        setAuth({
          token,
          user: response.data
        });
      } catch (error) {
        console.error('Auth verification failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuth(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      setAuth(null);
    };
    window.addEventListener('zone:unauthorized', onUnauthorized);
    return () => {
      window.removeEventListener('zone:unauthorized', onUnauthorized);
    };
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setAuth({ token, user });
    
    return user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setAuth(null);
    }
  };

  const updateUser = (userData) => {
    const updatedAuth = { ...auth, user: { ...auth.user, ...userData } };
    localStorage.setItem('user', JSON.stringify(updatedAuth.user));
    setAuth(updatedAuth);
  };

  const value = {
    auth,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!auth,
    isAdmin: auth?.user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
